import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { normalizeIdentifierHeader } from '@/shared/identifierResolution/standardIdentifierTypes';

export interface CustomIdentifierRow {
  id: string;
  organisation_id: string;
  name: string;
  maps_to: string | null;
  created_at: string;
}

const fromAny = (table: string) =>
  (supabase as unknown as { from: (t: string) => ReturnType<typeof supabase.from> }).from(table);

export function customNamesFromRows(rows: CustomIdentifierRow[] | null | undefined): string[] {
  return (rows ?? []).map((r) => r.name).filter(Boolean);
}

export function customCanonicalMap(
  rows: CustomIdentifierRow[] | null | undefined,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const row of rows ?? []) {
    if (row.maps_to) {
      map[normalizeIdentifierHeader(row.name)] = row.maps_to;
    }
  }
  return map;
}

export function useOrganisationCustomIdentifiers(organisationId: string | undefined) {
  return useQuery({
    queryKey: ['organisation-custom-identifiers', organisationId],
    enabled: Boolean(organisationId),
    queryFn: async (): Promise<CustomIdentifierRow[]> => {
      const { data, error } = await fromAny('organisation_custom_identifiers')
        .select('id, organisation_id, name, maps_to, created_at')
        .eq('organisation_id', organisationId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as CustomIdentifierRow[];
    },
  });
}
